import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import SyncIcon from '@material-ui/icons/Sync';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import GoogleCampaignSpendTable from '../../components/marketing/GoogleCampaignSpendTable';
import { googleReportCacheKey } from '../../components/marketing/marketingAdsCacheKey';
import { setMarketingAdsFilters, setMarketingAdsGoogle } from '../../redux/actions/marketingAdsAction';
import MetaAdsCampaignLoader from '../../components/marketing/MetaAdsCampaignLoader';
import MarketingSpendOverviewAccordion from '../../components/marketing/MarketingSpendOverviewAccordion';
import LeadChannelSwitch from './LeadChannelSwitch';
import MetaAdsLeadDialog, { downloadLeadRowsCsv } from '../../components/marketing/MetaAdsLeadDialog';
import SpendTrendChart from '../../components/marketing/SpendTrendChart';
import {
    ZONES,
    CITY_ZONE,
    citiesMatchingZones,
    zoneForCity,
} from '../../components/marketing/cityZones';
import { escapeCsv, formatMetric, formatMoney, formatNumber, localDate } from '../../components/marketing/format';
import {
    adsErrorMessage,
    dailySeries,
    getGoogleAdsLeadDetails,
    getGoogleAdsLeadTargets,
    getGoogleAdsReport,
    isCanceled,
    saveGoogleAdsCityTag,
    saveGoogleAdsLeadTarget,
    syncGoogleAdsReport,
} from '../../services/googleAdsService';

const LEAD_BUCKET_LABEL = {
    received: 'Leads received',
    sql: 'SQL-Real',
    tbc: 'Sales-TBC',
    real: 'Total Real',
    won: 'Sales Won',
};

const useStyles = makeStyles(() => ({
    root: {
        backgroundColor: '#f3f5f8',
        minHeight: '100%',
        padding: '4px 4px 24px',
    },
    page: {
        maxWidth: 1440,
        margin: '0 auto',
    },
    headerCard: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 16,
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        color: '#1C375D',
        margin: 0,
    },
    subtitle: {
        fontSize: 13,
        color: '#8a92a3',
        marginTop: 6,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    syncBtn: {
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '8px 18px',
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#152a47', boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: '#9aa8bc', color: '#f5f5f5' },
    },
    exportBtn: {
        backgroundColor: '#355025',
        color: '#FFF2EB',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '8px 18px',
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#28401c', boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: '#9aa8bc', color: '#f5f5f5' },
    },
    linkBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        color: '#1C375D',
    },
    filters: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 16,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    cityField: {
        minWidth: 220,
    },
    zoneField: {
        minWidth: 220,
    },
    alert: {
        padding: '12px 16px',
        borderRadius: 8,
        marginBottom: 16,
        fontSize: 14,
    },
    error: {
        background: '#fff1f0',
        color: '#a61b1b',
    },
    success: {
        background: '#edf9f0',
        color: '#176b35',
    },
    footnote: {
        margin: '8px 0 16px',
        color: '#8a92a3',
        fontSize: 12,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 16,
        marginBottom: 8,
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        minWidth: 0,
    },
    icon: {
        width: 36,
        height: 36,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1C375D14',
        color: '#1C375D',
        flexShrink: 0,
    },
    label: {
        fontSize: 11.5,
        color: '#8a92a3',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    value: {
        fontSize: 20,
        fontWeight: 700,
        color: '#1C375D',
        marginTop: 2,
        overflowWrap: 'anywhere',
    },
    loaderCard: {
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        marginBottom: 16,
    },
}));

function SummaryCard({ classes, icon, label, value, onClick }) {
    const clickable = typeof onClick === 'function';
    return (
        <article
            className={classes.card}
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            } : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            style={clickable ? { cursor: 'pointer' } : undefined}
        >
            <div className={classes.icon}>{icon}</div>
            <div style={{ minWidth: 0 }}>
                <div className={classes.label}>{label}</div>
                <div className={classes.value}>{value}</div>
            </div>
        </article>
    );
}

function sameCurrency(campaigns) {
    const codes = [...new Set(campaigns.map((campaign) => campaign.currency || 'INR'))];
    return {
        mixed: codes.length > 1,
        currency: codes[0] || 'INR',
    };
}

function campaignCities(campaign) {
    return campaign.cities?.length ? campaign.cities : [];
}

export default function GoogleAdsDashboard({ channel = 'google', onChannel }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const googleCache = useSelector((state) => state.marketingAds?.google);
    const today = localDate();
    const monthStart = `${today.slice(0, 7)}-01`;
    const mounted = useRef(true);

    const [from, setFrom] = useState(monthStart);
    const [to, setTo] = useState(today);
    const [city, setCity] = useState('all');
    const [zones, setZones] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [records, setRecords] = useState([]);
    const [daily, setDaily] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [leadDialog, setLeadDialog] = useState(null);
    const [taggingId, setTaggingId] = useState('');
    const [downloadingReal, setDownloadingReal] = useState(false);
    const [targets, setTargets] = useState({});

    const reportCacheKey = googleReportCacheKey(from, to);
    const hasCachedReport = Boolean(
        googleCache?.cacheKey === reportCacheKey && googleCache?.lastUpdated,
    );

    useEffect(() => () => {
        mounted.current = false;
    }, []);

    const loadReport = useCallback(async (signal) => {
        if (!from || !to || from > to || to > today) {
            setError('Select a valid date range.');
            setCampaigns([]);
            setRecords([]);
            setDaily([]);
            setLoaded(false);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const reportPromise = getGoogleAdsReport({ startDate: from, endDate: to, signal });
            const targetPromise = getGoogleAdsLeadTargets({ signal }).catch((targetError) => {
                if (isCanceled(targetError)) throw targetError;
                return null;
            });
            const [report, nextTargets] = await Promise.all([reportPromise, targetPromise]);
            if (signal?.aborted || !mounted.current) return;
            setCampaigns(report.campaigns);
            setRecords(report.records);
            setDaily(report.daily);
            if (nextTargets) setTargets(nextTargets);
            setLoaded(true);
            dispatch(setMarketingAdsGoogle({
                cacheKey: reportCacheKey,
                campaigns: report.campaigns,
                records: report.records,
                daily: report.daily,
                targets: nextTargets || {},
            }));
            dispatch(setMarketingAdsFilters({ from, to }));
        } catch (loadError) {
            if (isCanceled(loadError) || signal?.aborted) return;
            if (!mounted.current) return;
            setCampaigns([]);
            setRecords([]);
            setDaily([]);
            setLoaded(false);
            setError(adsErrorMessage(loadError, 'Unable to load the Google Ads report.'));
        } finally {
            if (!signal?.aborted && mounted.current) setLoading(false);
        }
    }, [from, to, today, reportCacheKey, dispatch, targets]);

    useEffect(() => {
        dispatch(setMarketingAdsFilters({ from, to }));
    }, [dispatch, from, to]);

    useEffect(() => {
        if (!hasCachedReport) return;
        setCampaigns(googleCache.campaigns || []);
        setRecords(googleCache.records || []);
        setDaily(googleCache.daily || []);
        setTargets(googleCache.targets || {});
        setLoaded(true);
        setLoading(false);
        setError('');
    }, [hasCachedReport, reportCacheKey, googleCache]);

    useEffect(() => {
        if (hasCachedReport) return undefined;
        if (!from || !to || from > to || to > today) {
            setLoaded(false);
            return undefined;
        }
        const controller = new AbortController();
        loadReport(controller.signal);
        return () => controller.abort();
    }, [hasCachedReport, reportCacheKey, from, to, today, loadReport]);

    const knownCities = useMemo(() => {
        const names = new Set();
        campaigns.forEach((campaign) => campaignCities(campaign).forEach((name) => names.add(name)));
        return [...names].sort((left, right) => left.localeCompare(right));
    }, [campaigns]);

    const zoneCityOptions = useMemo(
        () => citiesMatchingZones(knownCities, zones),
        [knownCities, zones],
    );

    useEffect(() => {
        if (city === 'all' || city === '__untagged') return;
        if (zones.length && !zones.includes(zoneForCity(city))) setCity('all');
    }, [city, zones]);

    const visibleCampaigns = useMemo(() => {
        const inZone = zones.length
            ? campaigns.filter((campaign) => campaignCities(campaign).some((name) => zones.includes(zoneForCity(name))))
            : campaigns;
        if (city === 'all') return inZone;
        if (city === '__untagged') return inZone.filter((campaign) => !campaignCities(campaign).length);
        return inZone.filter((campaign) => campaignCities(campaign).includes(city));
    }, [campaigns, city, zones]);

    const datedRecords = records.some((row) => row.date);
    const recordInScope = (row) => {
        const tags = row.cities || [];
        if (city === '__untagged') return tags.length === 0;
        if (city !== 'all') return tags.includes(city);
        if (zones.length) return tags.some((name) => zones.includes(zoneForCity(name)));
        return true;
    };
    const visibleRecords = useMemo(
        () => (datedRecords ? records.filter(recordInScope) : []),
        [records, datedRecords, city, zones]
    );
    const visibleDaily = useMemo(() => {
        if (!datedRecords) {
            if (city !== 'all' || zones.length) return [];
            return daily;
        }
        return dailySeries(records, recordInScope);
    }, [city, zones, daily, records, datedRecords]);

    const hasUntagged = !zones.length && campaigns.some((campaign) => !campaignCities(campaign).length);
    const cityLabel = city === '__untagged' ? 'Untagged' : city;
    const scopeLabel = city !== 'all'
        ? cityLabel
        : (zones.length ? zones.join(', ') : 'All cities');

    const { currency, mixed } = useMemo(() => sameCurrency(visibleCampaigns), [visibleCampaigns]);

    const totals = useMemo(() => {
        const spend = visibleCampaigns.reduce((sum, campaign) => sum + campaign.spend, 0);
        const clicks = visibleCampaigns.reduce((sum, campaign) => sum + campaign.clicks, 0);
        const impressions = visibleCampaigns.reduce((sum, campaign) => sum + campaign.impressions, 0);
        return {
            spend,
            clicks,
            impressions,
            cpc: !mixed && clicks > 0 ? spend / clicks : null,
            leadCount: visibleCampaigns.reduce((sum, campaign) => sum + (campaign.leadCount || 0), 0),
            sqlReal: visibleCampaigns.reduce((sum, campaign) => sum + (campaign.sqlReal || 0), 0),
            salesTbc: visibleCampaigns.reduce((sum, campaign) => sum + (campaign.salesTbc || 0), 0),
            totalReal: visibleCampaigns.reduce((sum, campaign) => sum + (campaign.totalReal || 0), 0),
            costPerReal: !mixed && visibleCampaigns.reduce((sum, campaign) => sum + (campaign.totalReal || 0), 0) > 0
                ? spend / visibleCampaigns.reduce((sum, campaign) => sum + (campaign.totalReal || 0), 0)
                : null,
        };
    }, [visibleCampaigns, mixed]);

    async function handleTargetSave(cityName, leadTarget) {
        try {
            await saveGoogleAdsLeadTarget({ city: cityName, leadTarget });
            if (!mounted.current) return;
            setTargets((current) => {
                const next = { ...current };
                if (leadTarget == null) delete next[cityName];
                else next[cityName] = leadTarget;
                dispatch(setMarketingAdsGoogle({
                    cacheKey: reportCacheKey,
                    campaigns,
                    records,
                    daily,
                    targets: next,
                }));
                return next;
            });
            setError('');
        } catch (saveError) {
            if (mounted.current) setError(adsErrorMessage(saveError, 'Unable to save the lead target.'));
            throw saveError;
        }
    }

    async function handleSync() {
        setSyncing(true);
        setError('');
        setNotice('');
        try {
            const result = await syncGoogleAdsReport({ startDate: from, endDate: to });
            if (!mounted.current) return;
            if (result?.skipped || result?.syncMode === 'noop') {
                setNotice(`Already synced through ${result.storedThrough || to}. No new days to fetch from Google.`);
            } else if (result?.syncMode === 'incremental') {
                setNotice(
                    `Incremental sync: ${result.fetched ?? 0} rows for ${result.fetchFrom} to ${result.fetchTo}`
                    + (result.storedThrough ? ` (after ${result.storedThrough}).` : '.'),
                );
            } else {
                const fetched = result?.fetched ?? result?.rows ?? result?.upserted ?? 0;
                setNotice(`Sync completed. ${fetched} reporting rows fetched.`);
            }
            await loadReport();
        } catch (syncError) {
            if (!mounted.current) return;
            setError(adsErrorMessage(syncError, 'Sync failed. Please try again.'));
        } finally {
            if (mounted.current) setSyncing(false);
        }
    }

    function exportCsv() {
        const headers = [
            'Campaign ID',
            'Campaign',
            'Status',
            'City',
            'Spend',
            'Currency',
            'Impressions',
            'Clicks',
            'Cost per click',
            'Leads received',
            'SQL-Real',
            'Sales-TBC',
            'Total Real',
            'CPERP',
        ];
        const rows = visibleCampaigns.map((campaign) => [
            campaign.campaignId,
            campaign.campaignName,
            campaign.status,
            campaign.cities?.join(', ') || '',
            campaign.spend,
            campaign.currency,
            campaign.impressions,
            campaign.clicks,
            campaign.costPerClick ?? '',
            campaign.leadCount ?? 0,
            campaign.sqlReal ?? 0,
            campaign.salesTbc ?? 0,
            campaign.totalReal ?? 0,
            campaign.totalReal > 0 ? campaign.spend / campaign.totalReal : '',
        ]);
        const csv = [headers, ...rows]
            .map((row) => row.map(escapeCsv).join(','))
            .join('\r\n');
        const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        const cityFile = city === 'all' ? 'all-cities' : cityLabel.toLowerCase().replace(/\s+/g, '-');
        link.download = `google-ads-${cityFile}-${from}-to-${to}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    async function downloadTotalReal() {
        if (downloadingReal || !visibleCampaigns.length) return;
        setDownloadingReal(true);
        setError('');
        try {
            const data = await getGoogleAdsLeadDetails({
                startDate: from,
                endDate: to,
                bucket: 'real',
                campaignIds: visibleCampaigns.map((campaign) => campaign.campaignId).filter(Boolean).join(','),
            });
            const rows = Array.isArray(data?.leads) ? data.leads : [];
            if (!rows.length) {
                setError('No Total Real leads found for this selection.');
                return;
            }
            downloadLeadRowsCsv(
                `Total Real · ${scopeLabel} · ${from}-to-${to}`,
                rows,
                'Form date',
                true
            );
        } catch (downloadError) {
            if (!mounted.current) return;
            setError(adsErrorMessage(downloadError, 'Unable to download Total Real leads.'));
        } finally {
            if (mounted.current) setDownloadingReal(false);
        }
    }

    function openLeads(bucket, campaignIds, label, day) {
        setLeadDialog({
            title: `${LEAD_BUCKET_LABEL[bucket] || 'Leads'} · ${label}`,
            query: {
                startDate: day || from,
                endDate: day || to,
                bucket,
                campaignIds: campaignIds?.length ? campaignIds.join(',') : undefined,
            },
        });
    }

    async function tagCity(campaignId, city) {
        const previousCampaigns = campaigns;
        const previousRecords = records;
        const apply = (row) => (
            String(row.campaignId) === String(campaignId)
                ? { ...row, cities: [city], city, citySource: 'manual' }
                : row
        );
        setTaggingId(campaignId);
        setCampaigns((current) => current.map(apply));
        setRecords((current) => current.map(apply));
        setError('');
        try {
            await saveGoogleAdsCityTag({ campaignId, city });
        } catch (tagError) {
            if (!mounted.current) return;
            setCampaigns(previousCampaigns);
            setRecords(previousRecords);
            setError(adsErrorMessage(tagError, 'Unable to save the campaign city.'));
        } finally {
            if (mounted.current) setTaggingId('');
        }
    }

    const money = (value) => {
        if (!loaded || mixed) return '—';
        return formatMetric(value, (amount) => formatMoney(amount, currency));
    };
    const rangeInvalid = !from || !to || from > to || to > today;
    const showPageLoader = syncing || (loading && !hasCachedReport);
    const loaderPhase = syncing && !loading ? 'sync' : 'campaigns';

    return (
        <section className={classes.root}>
            <div className={classes.page}>
                <header className={classes.headerCard}>
                    <div>
                        <LeadChannelSwitch value={channel} onChange={onChannel} />
                        <h1 className={classes.title}>Google Ads Expenditure</h1>
                        <p className={classes.subtitle}>
                            Campaign spend for the selected dates, totaled from one stored row per campaign and day.
                        </p>
                    </div>
                    <div className={classes.actions}>
                        <Button
                            className={classes.syncBtn}
                            variant="contained"
                            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                            onClick={handleSync}
                            disabled={syncing || loading || rangeInvalid}
                        >
                            {syncing ? 'Syncing...' : 'Sync from Google'}
                        </Button>
                        <Button
                            className={classes.exportBtn}
                            variant="contained"
                            startIcon={<GetAppIcon />}
                            onClick={exportCsv}
                            disabled={!visibleCampaigns.length || loading}
                        >
                            Export CSV
                        </Button>
                        <Button
                            className={classes.exportBtn}
                            variant="contained"
                            startIcon={downloadingReal ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                            onClick={downloadTotalReal}
                            disabled={!visibleCampaigns.length || loading || downloadingReal || !totals.totalReal}
                        >
                            {downloadingReal ? 'Downloading...' : 'Download Total Real'}
                        </Button>
                    </div>
                </header>

                <div className={classes.filters}>
                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={from}
                        inputProps={{ max: to < today ? to : today }}
                        onChange={(event) => setFrom(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={to}
                        inputProps={{ min: from, max: today }}
                        onChange={(event) => setTo(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl className={classes.zoneField} variant="outlined" size="small">
                        <InputLabel id="google-ads-zone-label">Zone</InputLabel>
                        <Select
                            labelId="google-ads-zone-label"
                            multiple
                            value={zones}
                            onChange={(event) => setZones(event.target.value)}
                            label="Zone"
                            renderValue={(selected) => (selected.length ? selected.join(', ') : 'All zones')}
                        >
                            {ZONES.map((name) => (
                                <MenuItem key={name} value={name}>
                                    <Checkbox checked={zones.indexOf(name) > -1} color="primary" />
                                    <ListItemText primary={name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        className={classes.cityField}
                        select
                        label="City"
                        size="small"
                        variant="outlined"
                        value={city}
                        onChange={(event) => setCity(event.target.value)}
                    >
                        <MenuItem value="all">All cities</MenuItem>
                        {zoneCityOptions.map((name) => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                        {hasUntagged && <MenuItem value="__untagged">Untagged</MenuItem>}
                    </TextField>
                </div>

                {error && (
                    <div className={`${classes.alert} ${classes.error}`} role="alert">{error}</div>
                )}
                {notice && (
                    <div className={`${classes.alert} ${classes.success}`} role="status">{notice}</div>
                )}

                {showPageLoader ? (
                    <div className={classes.loaderCard}>
                        <MetaAdsCampaignLoader active phase={loaderPhase} />
                    </div>
                ) : (
                    <>
                <MarketingSpendOverviewAccordion
                    title="Spend summary & daily chart"
                    hint={mixed ? 'Multiple currencies' : `Spend ${formatMoney(totals.spend, currency)} · Total Real ${formatNumber(totals.totalReal)}`}
                >
                <div className={classes.grid}>
                    <SummaryCard
                        classes={classes}
                        icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
                        label="Total spend"
                        value={mixed ? 'Multiple currencies' : formatMoney(totals.spend, currency)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<VisibilityOutlinedIcon fontSize="small" />}
                        label="Impressions"
                        value={formatNumber(totals.impressions)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Clicks"
                        value={formatNumber(totals.clicks)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<MonetizationOnOutlinedIcon fontSize="small" />}
                        label="Cost per click"
                        value={money(totals.cpc)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Leads received"
                        value={formatNumber(totals.leadCount)}
                        onClick={() => openLeads('received', visibleCampaigns.map((campaign) => campaign.campaignId), scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="SQL-Real"
                        value={formatNumber(totals.sqlReal)}
                        onClick={() => openLeads('sql', visibleCampaigns.map((campaign) => campaign.campaignId), scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Sales-TBC"
                        value={formatNumber(totals.salesTbc)}
                        onClick={() => openLeads('tbc', visibleCampaigns.map((campaign) => campaign.campaignId), scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<TouchAppIcon fontSize="small" />}
                        label="Total Real"
                        value={formatNumber(totals.totalReal)}
                        onClick={() => openLeads('real', visibleCampaigns.map((campaign) => campaign.campaignId), scopeLabel)}
                    />
                    <SummaryCard
                        classes={classes}
                        icon={<MonetizationOnOutlinedIcon fontSize="small" />}
                        label="CPERP"
                        value={money(totals.costPerReal)}
                    />
                </div>
                <p className={classes.footnote}>
                    Leads received use the form date in the selected range.
                    {' '}SQL-Real and Sales-TBC use the lead assigned date. Sales-TBC with no assigned date uses the form date.
                    {' '}Junk, Hold, and Move to Marketing are excluded. Total Real is SQL-Real plus Sales-TBC. Sales Won uses static sales won amount and stays on the form received date even if the deal closes later. CPERP is spend divided by Total Real.
                    {' '}Click a count, or a day on the chart, to open those leads. The campaign id comes from gad_campaignid on the page URL, then from the campaign number in utm_campaign.
                </p>
                <SpendTrendChart
                    daily={visibleDaily}
                    currency={currency}
                    mixedCurrency={mixed}
                    onDayClick={(date) => openLeads(
                        'received',
                        visibleCampaigns.map((campaign) => campaign.campaignId),
                        date,
                        date
                    )}
                    emptyMessage={!datedRecords && (city !== 'all' || zones.length)
                        ? 'Daily spend for one city needs one stored row per campaign and day.'
                        : 'No daily spend for this range. Sync from Google, or wait until the reporting API returns campaign-day rows.'}
                />
                </MarketingSpendOverviewAccordion>
                <GoogleCampaignSpendTable
                    campaigns={visibleCampaigns}
                    records={visibleRecords}
                    from={from}
                    to={to}
                    currency={currency}
                    mixedCurrency={mixed}
                    loading={false}
                    hasError={Boolean(error)}
                    scopeLabel={scopeLabel}
                    onOpenLeads={openLeads}
                    cityOptions={Object.keys(CITY_ZONE).sort((left, right) => left.localeCompare(right))}
                    onTagCity={tagCity}
                    taggingId={taggingId}
                    targets={targets}
                    onSaveTarget={handleTargetSave}
                    emptyMessage={city !== 'all'
                        ? `No campaigns tagged to ${cityLabel} in this date range.`
                        : (zones.length ? `No campaigns in ${zones.join(', ')} for this date range.` : '')}
                />
                    </>
                )}
                <MetaAdsLeadDialog
                    open={Boolean(leadDialog)}
                    title={leadDialog?.title || 'Leads'}
                    query={leadDialog?.query}
                    onClose={() => setLeadDialog(null)}
                    loadLeads={getGoogleAdsLeadDetails}
                    createdDateLabel="Form date"
                    showContact
                />
            </div>
        </section>
    );
}
